import type { ReactNode } from 'react';

import { CloseButton } from '@/components/common/close-button';

import styles from './overlay-chrome.module.scss';
import { OverlayPattern } from './overlay-pattern';

export type OverlayChromeProps = {
  /** When set, renders the cream header band with title + pattern. */
  title?: ReactNode;
  /** Optional muted line under the title, inside the header band (Figma 401:273008). */
  subtitle?: ReactNode;
  onClose: () => void;
  closeAriaLabel?: string;
  /** Footer node (typically <OverlayFooter />). Omitted when absent. */
  footer?: ReactNode;
  /** When provided, body + footer are wrapped in a <form> with this handler. */
  onSubmit?: React.ComponentProps<'form'>['onSubmit'];
  /**
   * Removes the body's padding **and** its scrolling, so the child owns both. Opt-in: every existing
   * overlay keeps the padded, scrolling body. For content that bleeds to the card's edges and scrolls
   * a pane of its own instead — the DM Configuration wizard's Preview step, whose cream tab rail runs
   * edge to edge while only the field pane scrolls.
   */
  flushBody?: boolean;
  /**
   * Renders the body as a white, top-rounded card floating over the cream header (Figma
   * `rFHoKFvO8DtXXMwDSfHKBn` node `1672:456851` — the Order Reviews panel, ADO 117773). Opt-in:
   * every existing overlay keeps its flat, edge-to-edge body — this only changes `.content`'s own
   * background (to cream, matching the header) and gives `.body` its own white fill + rounded top
   * corners, which is what makes the corner cut reveal the cream behind it rather than more white.
   */
  roundedBody?: boolean;
  children: ReactNode;
};

export function OverlayChrome({
  title,
  subtitle,
  onClose,
  closeAriaLabel = 'Close',
  footer,
  onSubmit,
  flushBody = false,
  roundedBody = false,
  children,
}: OverlayChromeProps) {
  const inner = (
    <>
      {title != null ? (
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle != null ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
          <OverlayPattern />
        </header>
      ) : null}
      {/* Skip the padded body entirely when there is nothing to put in it, so a header+footer
          overlay (e.g. a confirmation with no extra input) doesn't show an empty 48px gap. */}
      {children != null && children !== false ? (
        <div
          className={[
            styles.body,
            flushBody ? styles.bodyFlush : undefined,
            roundedBody ? styles.bodyCard : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {children}
        </div>
      ) : null}
      {footer ? <div className={styles.footerRegion}>{footer}</div> : null}
    </>
  );

  return (
    <div
      className={[styles.content, roundedBody ? styles.contentFramed : undefined]
        .filter(Boolean)
        .join(' ')}
    >
      <CloseButton aria-label={closeAriaLabel} onClick={onClose} className={styles.close} />
      {onSubmit ? (
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {inner}
        </form>
      ) : (
        inner
      )}
    </div>
  );
}
